import React, { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import styled from "styled-components";
import {
  Button,
  ButtonGroup,
  Callout,
  ControlGroup,
  FileInput,
  FormGroup,
  InputGroup,
  TextArea,
} from "@blueprintjs/core";

import { Code } from "@components/Code";
import {
  OpenIdProviderData,
  SamlProviderData,
  SsoProviderSettings,
  SsoProviderType,
} from "@types";

const WarnText = styled.p`
  font-size: var(--global-font-size-xs);
  color: var(--global-red);
`;

const StyledCode = styled(Code)`
  box-shadow: None;
  border: solid 1px var(--global-green);
`;

const MarginButton = styled(Button)`
  margin-top: 15px; // 15px to match blueprint's margin after FormGroup
`;

const FormSectionHeading = styled.h4`
  padding: 0.5em 0 0 0;
`;

type Props = {
  deploymentId: string | null;
  emailDomain: string;
  providerId: string;
  handleSaveSSO: (ssoSettings: SsoProviderSettings) => void;
  disabled?: boolean;
  skipBaseUrlValidation?: boolean;
};

type ProviderData = SamlProviderData | OpenIdProviderData;

/**
 * This form includes fields to collect all data necessary for
 * the SsoProviderSettings object except deployment id,
 * email domain, and provider id (which must be passed in). If
 * creating a new deployment, the deployment id should be null.
 *
 * Calls the given `handleSaveSSO` function and passes it the
 * filled-in SsoProviderSettings when the uses clicks the
 * submit button. This function should make any API calls
 * necessary to do what is intended with the data.
 *
 * This component does not make any API calls---it is purely
 * the form itself.
 *
 * If `skipBaseUrlValidation` is true, the base url is not
 * checked for allow-listing.
 *
 */
export const SsoSetupForm: React.FC<Props> = ({
  deploymentId,
  emailDomain,
  providerId,
  handleSaveSSO,
  disabled,
  skipBaseUrlValidation,
}) => {
  const [displayName, setDisplayName] = useState<string>("");
  const [baseUrl, setBaseUrl] = useState<string>("");
  const [baseUrlIsValid, setBaseUrlIsValid] = useState<boolean>(true);
  const [providerData, setProviderData] = useState<ProviderData>({
    clientId: "",
    clientSecret: "",
  });
  const [providerType, setProviderType] = useState<SsoProviderType>("openid");

  const handleBaseUrlChange = useMemo(
    () =>
      debounce(
        () =>
          setBaseUrlIsValid(
            skipBaseUrlValidation ||
              baseUrl.includes("okta") ||
              baseUrl.includes("google") ||
              baseUrl.includes("auth0")
          ),
        800
      ),
    [skipBaseUrlValidation, baseUrl]
  );

  const updateProviderDataMemoized = useCallback(
    (pd: ProviderData) => setProviderData(pd),
    []
  );

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSaveSSO({
            deployment_id: deploymentId,
            provider_id: providerId,
            provider_type: providerType,
            display_name: displayName,
            base_url: baseUrl,
            email_domain: emailDomain,
            provider_data: providerData,
          });
        }}
      >
        <FormGroup
          label="Display Name"
          disabled={disabled}
          helperText="A human-friendly name for your SSO configuration."
        >
          <InputGroup
            placeholder={`e.g. ${providerId} SSO`}
            required
            value={displayName}
            onChange={(e: React.FormEvent<HTMLElement>) => {
              setDisplayName((e.target as HTMLInputElement).value);
            }}
            disabled={disabled}
          />
        </FormGroup>
        <FormGroup label="SSO Method">
          <ButtonGroup fill={true} style={{ marginTop: 5 }}>
            <Button
              active={providerType === "openid"}
              text="OpenID Connect / Oauth2"
              onClick={() => setProviderType("openid")}
            />
            <Button
              active={providerType === "saml2"}
              text="SAML2"
              onClick={() => setProviderType("saml2")}
            />
          </ButtonGroup>
        </FormGroup>
        <Callout>
          {providerType === "openid" ? (
            <OpenIDForm
              providerId={providerId}
              handleUpdate={updateProviderDataMemoized}
              disabled={disabled}
            />
          ) : providerType === "saml2" ? (
            <SamlForm
              providerId={providerId}
              handleUpdate={updateProviderDataMemoized}
              disabled={disabled}
            />
          ) : (
            "Please choose a provider type using the above buttons"
          )}
          <FormGroup label="Base URL or Domain" disabled={disabled}>
            <InputGroup
              placeholder="e.g. dev-6517719.okta.com"
              required
              value={baseUrl}
              onChange={(e: React.FormEvent<HTMLElement>) => {
                setBaseUrl((e.target as HTMLInputElement).value);
                handleBaseUrlChange();
              }}
              disabled={disabled}
            />
            {!baseUrlIsValid && (
              <WarnText>
                Please{" "}
                <a
                  href="https://r2c.dev/slack"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact us
                </a>{" "}
                if you would like to add a provider besides Auth0/Okta/Google.
              </WarnText>
            )}
          </FormGroup>
        </Callout>
        <FormGroup disabled={disabled}>
          <MarginButton type="submit" intent="primary" disabled={disabled}>
            Create/Update Auth Provider
          </MarginButton>
        </FormGroup>
      </form>
    </div>
  );
};

type SsoFormProps = {
  providerId: string;
  handleUpdate: (providerData: ProviderData) => void;
  disabled?: boolean;
};

const OpenIDForm: React.FC<SsoFormProps> = ({
  providerId,
  handleUpdate,
  disabled,
}) => {
  const [clientId, setClientId] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string>("");

  useEffect(() => {
    handleUpdate({
      clientId: clientId,
      clientSecret: clientSecret,
    });
  }, [clientId, clientSecret, handleUpdate]);

  return (
    <>
      <FormSectionHeading>Copy to SSO provider:</FormSectionHeading>
      <FormGroup label="Redirect URL">
        <StyledCode light copyable>
          https://{window.location.hostname}/oauth/callback/{providerId}
        </StyledCode>
      </FormGroup>
      <FormSectionHeading>Copy from SSO provider:</FormSectionHeading>
      <FormGroup label="Client ID">
        <InputGroup
          placeholder="required"
          required
          value={clientId}
          onChange={(e: React.FormEvent<HTMLElement>) => {
            setClientId((e.target as HTMLInputElement).value);
          }}
          disabled={disabled}
        />
      </FormGroup>
      <FormGroup label="Client Secret">
        <InputGroup
          placeholder="required"
          required
          value={clientSecret}
          type="password"
          onChange={(e: React.FormEvent<HTMLElement>) => {
            setClientSecret((e.target as HTMLInputElement).value);
          }}
          disabled={disabled}
        />
      </FormGroup>
    </>
  );
};

const SamlForm: React.FC<SsoFormProps> = ({
  providerId,
  handleUpdate,
  disabled,
}) => {
  const [idpSsoUrl, setIdpSsoUrl] = useState<string>("");
  const [idpIssuer, setIdpIssuer] = useState<string>("");
  const [x509cert, setX509cert] = useState<string>("");

  useEffect(() => {
    handleUpdate({
      idpSsoUrl: idpSsoUrl,
      idpIssuer: idpIssuer,
      x509cert: x509cert,
    });
  }, [idpSsoUrl, idpIssuer, x509cert, handleUpdate]);

  return (
    <>
      <FormSectionHeading>Copy to SSO provider:</FormSectionHeading>
      <FormGroup label="SSO URL" disabled={disabled}>
        <StyledCode light copyable>
          https://{window.location.hostname}/api/auth/saml/{providerId}
        </StyledCode>
      </FormGroup>
      <FormGroup label="Audience URL (SP Entity ID)" disabled={disabled}>
        <StyledCode light copyable>
          semgrep-dev
        </StyledCode>
      </FormGroup>
      <FormSectionHeading>Copy from SSO provider:</FormSectionHeading>
      <FormGroup label="IdP SSO URL" disabled={disabled}>
        <InputGroup
          placeholder="required"
          required
          value={idpSsoUrl}
          onChange={(e: React.FormEvent<HTMLElement>) => {
            setIdpSsoUrl((e.target as HTMLInputElement).value);
          }}
          disabled={disabled}
        />
      </FormGroup>
      <FormGroup label="IdP Issuer ID" disabled={disabled}>
        <InputGroup
          placeholder="required"
          required
          value={idpIssuer}
          onChange={(e: React.FormEvent<HTMLElement>) => {
            setIdpIssuer((e.target as HTMLInputElement).value);
          }}
          disabled={disabled}
        />
      </FormGroup>
      <FormGroup label="X.509 Certificate" disabled={disabled}>
        <ControlGroup vertical>
          <FileInput
            text={
              x509cert.length === 0
                ? "Upload certificate"
                : "Change certificate"
            }
            onInputChange={(e) => {
              const file = (e.target as HTMLInputElement).files?.[0];
              const reader = new FileReader();
              reader.onload = (e) => {
                setX509cert(e.target?.result as string);
              };
              if (file) {
                reader.readAsText(file);
              }
            }}
            disabled={disabled}
          />
          <br />
          <TextArea
            placeholder="or paste certificate here"
            required
            cols={50}
            value={x509cert}
            onChange={(e: React.FormEvent<HTMLElement>) => {
              setX509cert((e.target as HTMLInputElement).value);
            }}
            disabled={disabled}
          />
        </ControlGroup>
      </FormGroup>
    </>
  );
};
